/*
  Japyara "Filter by" bar (sections/jp-collection.liquid + snippets/jp-collection-filters.liquid).
  Filtering, chips, sorting and pagination stay with Broadcast's collection-filters-form.js and
  collection.js; this only adds the desktop dropdown behaviour on top of the same markup:
  - .jp-bar on the section = bar layout. Always from 990px; from 750px only if the bar fits on one
    line; otherwise (and on mobile) Broadcast's slide-out drawer is used.
  - One dropdown open at a time; closes on outside click and Esc; reopened after Broadcast swaps the
    filter markup when results update.
  - Price fields apply when committed (change / Enter) instead of while typing; the slider
    applies on release (Broadcast's theme:range:update).
*/
(() => {
  const DESKTOP = 990;
  const TABLET = 750;

  const init = (section) => {
    if (section.dataset.jpBarReady) return;
    section.dataset.jpBarReady = 'true';

    const sidebar = section.querySelector('[data-collection-sidebar]');
    if (!sidebar) return;

    let openKey = null;
    let focusId = null;

    const isBar = () => section.classList.contains('jp-bar');
    const groups = () => [...sidebar.querySelectorAll('.filter-group')];
    const keyOf = (group) => {
      const span = group.querySelector('.filter-group__heading span');
      return span ? span.textContent.trim() : '';
    };

    const refreshSliders = () => {
      sidebar.querySelectorAll('range-slider').forEach((slider) => {
        if (typeof slider.connectedCallback === 'function') slider.connectedCallback();
      });
    };

    // Keep every panel (closed ones too, they are only invisible) inside the viewport: groups near
    // the right edge open to the left, so nothing widens the page
    const placePanel = (group) => {
      const body = group.querySelector('.filter-group__body');
      group.classList.remove('is-flipped');
      if (body && body.getBoundingClientRect().right > document.documentElement.clientWidth - 16) {
        group.classList.add('is-flipped');
      }
    };

    const setOpen = (group, open) => {
      group.classList.toggle('is-open', open);
      const summary = group.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) return;
      // The price slider positions its handles from its measured width: re-measure now it is laid out
      const slider = group.querySelector('range-slider');
      if (slider && typeof slider.connectedCallback === 'function') slider.connectedCallback();
      placePanel(group);
    };

    const closeAll = () => {
      groups().forEach((group) => setOpen(group, false));
      openKey = null;
    };

    // Bar layout needs every <details> open; visibility of the panels is handled by .is-open
    const prepareBar = () => {
      groups().forEach((group) => {
        const details = group.querySelector('details');
        if (details && !details.open) details.open = true;
        const body = group.querySelector('[data-collapsible-body]');
        if (body) body.style.height = 'auto';
        const summary = group.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', group.classList.contains('is-open') ? 'true' : 'false');
        placePanel(group);
      });
    };

    // Compare the group buttons (not their hidden panels) with the row's right edge
    const fits = () => {
      const row = sidebar.querySelector('.jp-filters__row');
      if (!row) return true;
      const limit = row.getBoundingClientRect().right + 1;
      return groups().every((group) => {
        const box = group.getBoundingClientRect();
        return box.width === 0 || box.right <= limit;
      });
    };

    const applyMode = () => {
      const width = window.innerWidth;
      const wasBar = isBar();
      let bar = width >= DESKTOP;
      if (!bar && width >= TABLET) {
        section.classList.add('jp-bar');
        bar = fits();
      }
      section.classList.toggle('jp-bar', bar);

      if (bar && !wasBar) {
        // Leaving drawer mode: close Broadcast's drawer if it was open (unlocks scrolling)
        if (sidebar.classList.contains('expanded')) {
          section.dispatchEvent(new CustomEvent('theme:filter:close', { bubbles: false }));
        }
      }
      if (bar) prepareBar();
      if (!bar && wasBar) closeAll();
      if (bar !== wasBar) refreshSliders();
    };

    // Dropdown toggles (capture phase: Broadcast's accordion handler never sees bar clicks)
    sidebar.addEventListener(
      'click',
      (event) => {
        if (!isBar()) return;
        const summary = event.target.closest('summary.filter-group__heading');
        if (!summary || !sidebar.contains(summary)) return;
        event.preventDefault();
        event.stopPropagation();
        const group = summary.closest('.filter-group');
        const open = !group.classList.contains('is-open');
        closeAll();
        if (open) {
          setOpen(group, true);
          openKey = keyOf(group);
        }
      },
      true
    );

    document.addEventListener('click', (event) => {
      if (!isBar() || openKey === null) return;
      const open = sidebar.querySelector('.filter-group.is-open');
      if (open && !open.contains(event.target)) closeAll();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isBar() || openKey === null) return;
      const open = sidebar.querySelector('.filter-group.is-open');
      closeAll();
      const summary = open && open.querySelector('summary');
      if (summary) summary.focus();
    });

    // Price fields: apply on commit only
    sidebar.addEventListener(
      'input',
      (event) => {
        if (event.target.matches('.filter__price__input') && !event.jpCommit) event.stopImmediatePropagation();
      },
      true
    );
    const commitPrice = (field) => {
      const commit = new Event('input', { bubbles: true });
      commit.jpCommit = true;
      field.dispatchEvent(commit);
    };
    sidebar.addEventListener('change', (event) => {
      if (event.target.matches('.filter__price__input')) commitPrice(event.target);
    });
    sidebar.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        const field = document.activeElement;
        if (field && field.matches && field.matches('.filter__price__input')) field.blur();
      },
      true
    );

    sidebar.addEventListener('focusin', (event) => {
      focusId = event.target.id || null;
    });

    // Broadcast replaced the filter markup after an update: restore the open dropdown and focus
    new MutationObserver(() => {
      if (!isBar()) return;
      prepareBar();
      if (openKey !== null) {
        const group = groups().find((item) => keyOf(item) === openKey);
        if (group) setOpen(group, true);
        else openKey = null;
      }
      if (focusId) {
        const field = document.getElementById(focusId);
        if (field && sidebar.contains(field)) field.focus({ preventScroll: true });
      }
    }).observe(sidebar, { childList: true });

    applyMode();
    if (isBar()) prepareBar();
    window.addEventListener('load', refreshSliders);
    document.addEventListener('theme:resize:width', applyMode);
    window.addEventListener('resize', () => {
      window.clearTimeout(section.jpBarTimer);
      section.jpBarTimer = window.setTimeout(applyMode, 150);
    });
  };

  const initAll = () => document.querySelectorAll('.jp-collection--filterbar').forEach(init);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
